import React, { Fragment, useContext, useEffect, useState } from "react";
import { OperationVariables } from "@apollo/client";
import { useLazyQuery, useMutation } from "@apollo/client/react";
import { GetTokenByCode, GetTokenByCodeVariables } from "./types/GetTokenByCode";
import getTokenBycodeQuery from "./getTokenBycodeQuery";
import authService, { SET_AUTH } from "../../services/authService";
import * as jwt from "jsonwebtoken";
import queryString from "query-string";
import { Box, Button, Grid, Typography } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import get from "lodash/get";
import { GetOptions } from "../App/types/GetOptions";
import GetOptionsQuery from "../App/GetOptionsQuery";
import advancedSearch from "../../model/advanced-search";
import { getCpdProviders } from "./types/getCpdProviders";
import GetCpdProvidersQuery from "./GetCpdProvidersQuery";
import MainNavigation from "../MainNavigation/MainNavigation";
import AuthLayout from "../common/AuthLayout/AuthLayout";
import Loading from "../Loading/Loading";
import styles from "./LoginPage.module.css";
import AppContext from "../../context/AppContext";
import { NO_DB_CONNECTION } from "../../constants";
import ErrorPopover from "../common/ErrorPopover/ErrorPopover";
import { EnrolUserMutation, EnrolUserMutationVariables } from "./types/enrolUserMutation";
import enrolUserMutation from "./enrolUserMutation";
import { RefetchCognitoTokenMutation, RefetchCognitoTokenMutationVariables } from "./types/refetchTokenMutation";
import refetchTokenMutation from "./refetchTokenMutation";
import { client } from "../../gqlClient";

import { UpdateUserDeviceSettings, UpdateUserDeviceSettingsVariables } from "./types/UpdateUserDeviceSettings";
import UpdateUserDeviceSettingsMutation from "./UpdateUserDeviceSettingsMutation";
import { storeMobileDeviceToken } from "./storeMobileDeviceToken";

export const CognitoLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const useragent = navigator.userAgent;
  const location = useLocation();
  const qs = queryString.parse(location.search);
  const isDesktop = useContext(AppContext).isDesktop;

  // const [cognitoCode, setCognitoCode] = useState("");
  const [optionsLoaded, setOptionsLoaded] = useState(false);
  const [cpdLoaded, setCpdLoaded] = useState(false);
  const setError = useContext(AppContext).setError;
  const [validationError, setValidationError] = useState(false);
  // Guard so auto-enrolment is only attempted once per page load (prevents an
  // enrol -> refetch -> still-not-enrolled -> enrol loop).
  const autoEnrolAttempted = React.useRef(false);

  const mobility = qs.ref === "mobility";
  if (mobility && !authService.isMobility()) {
    //Set mobility if not yet set
    authService.setMobility(mobility);
  }

  const [getToken, { data: tokenData, error: tokenError }] = useLazyQuery<GetTokenByCode, GetTokenByCodeVariables>(
    getTokenBycodeQuery,
  );
  const [siteOptions, { data: sitesOptionsData }] = useLazyQuery<GetOptions, OperationVariables>(GetOptionsQuery);
  const [cpdProviders, { data: cpdProvidersData }] = useLazyQuery<getCpdProviders, OperationVariables>(
    GetCpdProvidersQuery,
  );
  const [enrolUser] = useMutation<EnrolUserMutation, EnrolUserMutationVariables>(enrolUserMutation);
  const [updateUsersDeviceConfig] = useMutation<UpdateUserDeviceSettings, UpdateUserDeviceSettingsVariables>(
    UpdateUserDeviceSettingsMutation,
  );
  const [refetchToken, { data: refetchTokenData }] = useMutation<
    RefetchCognitoTokenMutation,
    RefetchCognitoTokenMutationVariables
  >(refetchTokenMutation);

  useEffect(() => {
    setError("");
    if (tokenError) {
      // setError(tokenError.message);
      setValidationError(true);
    }
    if (tokenData) {
      authService.persistCognitoToken(
        tokenData.getTokenByCode.access_token,
        tokenData.getTokenByCode.id_token,
        tokenData.getTokenByCode.refresh_token,
      );

      const idToken: any = jwt.decode(tokenData.getTokenByCode.id_token);

      const validationError = idToken["custom:validationError"];

      if (validationError) {
        setValidationError(true);
      } else {
        const loginResult = tokenData.getTokenByCode.loginResult;
        if (idToken && idToken["custom:eresults"] && loginResult) {
          const resultData = JSON.parse(idToken["custom:eresults"]);
          authService.persistAllValues(loginResult);

          authService.persistCognitoToken(
            tokenData.getTokenByCode.access_token,
            tokenData.getTokenByCode.id_token,
            tokenData.getTokenByCode.refresh_token,
          );

          // Persist to localStorage if "Remember me" was checked
          if (authService.getRememberMe() && tokenData.getTokenByCode.refresh_token) {
            authService.persistCognitoTokenToLocalStorage(
              tokenData.getTokenByCode.refresh_token,
            );
          }

          client.cache.writeQuery({
            query: SET_AUTH,
            data: { auth: { isAuth: true, __typename: "Auth" } },
          });

          if (!resultData.cognitoEnrolled) {
            // Auto-enrol into SSO without the consent prompt: enrol the user,
            // then refetch tokens (which will come back cognitoEnrolled = true
            // and continue the normal login flow). Guarded to run only once.
            if (!autoEnrolAttempted.current) {
              autoEnrolAttempted.current = true;
              enrollUserNow();
            }
          } else {
            siteOptions({});
            cpdProviders();
          }
        }
      }
    }

    if (
      refetchTokenData &&
      refetchTokenData.refetchCognitoToken.access_token &&
      refetchTokenData.refetchCognitoToken.id_token
    ) {
      const idToken: any = jwt.decode(refetchTokenData.refetchCognitoToken.id_token);
      const loginResult = refetchTokenData.refetchCognitoToken.loginResult;
      if (idToken && idToken["custom:eresults"] && loginResult) {
        const resultData = JSON.parse(idToken["custom:eresults"]);
        authService.persistAllValues(loginResult);

        authService.persistCognitoToken(
          refetchTokenData.refetchCognitoToken.access_token,
          refetchTokenData.refetchCognitoToken.id_token,
          refetchTokenData.refetchCognitoToken.refresh_token || "",
        );

        // Persist to localStorage if "Remember me" was checked
        if (authService.getRememberMe() && refetchTokenData.refetchCognitoToken.refresh_token) {
          authService.persistCognitoTokenToLocalStorage(
            refetchTokenData.refetchCognitoToken.refresh_token,
          );
        }

        client.cache.writeQuery({
          query: SET_AUTH,
          data: { auth: { isAuth: true, __typename: "Auth" } },
        });

        if (!resultData.cognitoEnrolled) {
          // Auto-enrol into SSO without the consent prompt (see note above).
          if (!autoEnrolAttempted.current) {
            autoEnrolAttempted.current = true;
            enrollUserNow();
          }
        } else {
          siteOptions({});
          cpdProviders();
        }
      }
    }

    if (sitesOptionsData) {
      const siteOptions = sitesOptionsData;
      authService.setSiteOptions(siteOptions.getOptions);
      setOptionsLoaded(true);
    }

    if (cpdProvidersData) {
      const cpdProviders = cpdProvidersData;
      authService.setCpdProviders(cpdProviders.getCpdProviders);
      setCpdLoaded(true);
    }

    if (optionsLoaded && cpdLoaded) {
      const completeLogin = async () => {
        const isCtFrom = authService.isClinicalTrial() && !mobility ? "/patient-episodes" : "/patients";
        let from: any = get(location, "state.from") || isCtFrom;

        if (authService.getDefaultWardCode()) {
          const wards = authService.getWards();

          const AdSearch: advancedSearch = new advancedSearch();
          AdSearch.wardCode = wards.find((w) => w.code === authService.getDefaultWardCode());
          const newquerystring = `?${AdSearch.toQueryString()}`;
          // eslint-disable-next-line no-const-assign
          from = get(location, "state.from") || `/patients${newquerystring}`;
        }

        const isSessionStorageUpdated = authService.token() !== null;

        if (isSessionStorageUpdated) {
          await storeMobileDeviceToken(updateUsersDeviceConfig, cpdProviders);
          navigate(from, { replace: true });
        }
      };
      completeLogin();
    }
  }, [tokenData, tokenError, sitesOptionsData, cpdProvidersData, optionsLoaded, cpdLoaded, refetchTokenData]);

  // const isEmptyObject = (obj: any) => {
  //   if (obj === null || typeof obj !== "object") {
  //     return false; // Or throw an error, depending on desired behavior
  //   }

  //   return Object.keys(obj).length === 0;
  // };

  useEffect(() => {
    const fetchData = async () => {
      const code = (qs.code || "").toString();
      if (code) {
        // setCognitoCode(code);
        // First get the token
        await getToken({
          variables: {
            input: {
              code: code,
              mobility,
              useragent,
            },
          },
        });

        // Wait for tokenData to be processed in the other useEffect
        // Then call the other functions

        // } else {
        //   if (isEmptyObject(qs)) {
        //     window.location.href = `${import.meta.env.VITE_LOGIN_URL}`;
        //   } else {
        //     window.location.href = `${import.meta.env.VITE_LOGOUT_URL}`;
        //   }
      } else if (qs.autologin || authService.getPersistedCognitoTokens()) {
        // Auto-login: restore persisted refresh token to sessionStorage and use refetchToken
        const persistedTokens = authService.getPersistedCognitoTokens();
        if (persistedTokens) {
          sessionStorage.setItem("cognito-refresh-token", persistedTokens.refreshToken);
        }
        try {
          await refetchToken({
            variables: {
              input: {
                mobility,
                useragent,
              },
            },
          });
        } catch {
          // Auto-login failed - clear stale tokens and redirect to login
          authService.clearToken();
          authService.clearPersistedCognitoTokens();
          navigate("/login", { replace: true });
        }
      } else if (!qs.ref) {
        // No code and no relevant params - redirect to main login
        navigate("/login", { replace: true });
      }
    };

    fetchData();
  }, []);

  const navigateToLogin = () => {
    navigate("/login");
  };

  // Auto-enrol the user into SSO (no consent prompt), then refetch tokens so the
  // flow continues as an enrolled user. On failure, surface the DB error.
  const enrollUserNow = async () => {
    try {
      const response = await enrolUser();
      if (response.data) {
        await refetchToken({
          variables: {
            input: {
              mobility,
              useragent,
            },
          },
        });
      }
    } catch {
      setError(NO_DB_CONNECTION);
    }
  };

  return (
    <Fragment>
      <MainNavigation hideTopMenu isAnnouncementAvai={false} />
      <Grid container className={isDesktop ? styles.mainBox : styles.mainBoxM}>
        <Grid size={{ xs: 12, md: 12 }}>
          <AuthLayout title="Sign In" maxWidth={"490px"} isAnnouncementAvai={false}>
            <Box mt={2}>
              {!validationError && (
                <>
                  <Typography>Authenticating single sign on... Please wait</Typography>
                  <Loading></Loading>
                </>
              )}

              {validationError && (
                <>
                  <Box mt={2} display="flex" flexDirection={"column"} justifyContent="center" alignItems="center">
                    <Typography>
                      We&apos;re sorry — your account is not associated with a supported hospital or organization.
                    </Typography>
                    <Typography>
                      To access this system, your hospital must be part of our approved partner network.
                    </Typography>
                  </Box>
                  <Box mt={2} display="flex" justifyContent="space-around" alignItems="center">
                    <Button variant="contained" color="secondary" onClick={() => navigateToLogin()}>
                      Back to Login
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          </AuthLayout>
        </Grid>
      </Grid>
      <ErrorPopover />
    </Fragment>
  );
};

export default CognitoLoginPage;
